import { Component, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, ExternalLink, ShieldCheck, Signature } from 'lucide-react';
import { api } from '../api.ts';
import { ago, fmtDate } from '../util.ts';

// The digest is another system's data: trim nanosecond timestamps and never let a bad date throw.
const safeIso = (v: string | undefined) => { if (!v) return null; const t = v.replace(/(\.\d{3})\d+/, '$1'); return Number.isNaN(Date.parse(t)) ? null : t; };
const when = (v: string | undefined) => { const t = safeIso(v); return t ? ago(t) : 'unknown'; };
const plain = (v: string | undefined) => (v ?? '').replace(/`/g, '');
const day = (v: string | undefined) => { const t = safeIso(v); return t ? fmtDate(t.slice(0, 10)) : v ?? ''; };

type Digest = {
  schema: string; generated_at: string;
  fleet_headline: { total_walls: number; healthy_walls: number; active_os_version: string; socket_lane_promoted: string; aware_published_version: string; aware_ed25519_verified: boolean };
  overnight_findings: { id: string; severity: string; title: string; summary: string; observed_at: string }[];
  pending_owner_gates: { id: string; issue_ref: string; title: string; status: string; blocking_note?: string }[];
  active_audit_blockers: { id: string; severity: string; title: string; related_pr_or_issue?: string }[];
  operational_countdowns: { id: string; title: string; status_badge: string; target_date: string; severity: string }[];
};

// Read-only view of Sconce engineering from sconce.tech/admin; nothing here can change Sconce.
export function SconceCard() {
  return <Boundary><SconceCardInner /></Boundary>;
}

class Boundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  render() {
    return this.state.failed ? <section className="card sconce"><div className="card-h"><h2>Sconce fleet &amp; engineering gate</h2></div><p className="muted sm">The Sconce digest couldn’t be displayed.</p></section> : this.props.children;
  }
}

function SconceCardInner() {
  const q = useQuery({ queryKey: ['sconce-digest'], queryFn: () => api<{ digest: Digest }>('/sconce/digest'), staleTime: 60_000, refetchInterval: 120_000, retry: false });
  const d = q.data?.digest;
  return (
    <section className="card sconce">
      <div className="card-h">
        <h2><ShieldCheck size={14} aria-hidden /> Sconce fleet &amp; engineering gate</h2>
        <a className="sm" href="https://sconce.tech/admin" target="_blank" rel="noreferrer">Admin <ExternalLink size={11} aria-hidden /></a>
      </div>
      {q.error && <p className="err sm">{(q.error as Error).message}</p>}
      {!d && !q.error && <p className="muted sm">Loading…</p>}
      {d && (
        <>
          <div className="sc-stats">
            <div className={d.fleet_headline.healthy_walls === d.fleet_headline.total_walls ? 'ok' : 'bad'}>
              <b>{d.fleet_headline.healthy_walls}/{d.fleet_headline.total_walls}</b><span>Walls healthy</span></div>
            <div><b>{d.fleet_headline.socket_lane_promoted.split(' ')[0]}</b><span>Sockets promoted</span></div>
            <div><b>{d.pending_owner_gates.length}</b><span>Owner gates</span></div>
          </div>
          <p className="muted sm sc-versions">
            OS {d.fleet_headline.active_os_version.split('-')[0]} · Aware {d.fleet_headline.aware_published_version}
            {d.fleet_headline.aware_ed25519_verified && <span className="ok"> <CheckCircle2 size={11} aria-hidden /> signed</span>}
          </p>
          {d.pending_owner_gates.length > 0 && (
            <ul className="sc-list">
              {d.pending_owner_gates.map((g) => (
                <li key={g.id} title={g.blocking_note}><Signature size={13} aria-hidden /><span><b>{g.issue_ref}</b> {plain(g.title)}<em> · {plain(g.status)}</em></span></li>
              ))}
            </ul>
          )}
          {d.active_audit_blockers.map((b) => (
            <p key={b.id} className="sc-blocker sm"><AlertTriangle size={12} aria-hidden /> {plain(b.title)}{b.related_pr_or_issue ? ` (${plain(b.related_pr_or_issue)})` : ''}</p>
          ))}
          {d.operational_countdowns.slice(0, 2).map((c) => (
            <p key={c.id} className="muted sm">{plain(c.title)} · {plain(c.status_badge)} · {day(c.target_date)}</p>
          ))}
          <p className="muted sm">Updated {when(d.generated_at)}</p>
        </>
      )}
    </section>
  );
}
